import { Injectable, computed, inject, signal, Signal } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import {
  AssignActivityAsGoogleClassroomAttachmentGQL,
  AssignActivityAsGoogleClassroomAttachmentMutation,
  AssignActivityToGoogleClassroomAssignmentDto,
} from '../../../generated/graphql';
import { NetworkCallService } from '@common/services/network-call.service';
import { Observable } from 'rxjs';
import { MutationResult } from 'apollo-angular';
import { Store, select, State, Action, StateContext } from '@ngxs/store';
import { LoggedInUserState } from '@modules/auth/store/loggedInUser.state';
import { LogoutUser } from '@modules/auth/store/loggedInUser.actions';
import { toSignal } from '@angular/core/rxjs-interop';

export interface GoogleClassroomIframeAddonAttachmentQueryParams {
  courseId: string; // The ID of the current Classroom course.
  itemId: string; // A unique identifier for the CourseWork, CourseWorkMaterial or Announcement that receives the add-on attachment.
  itemType?: string; // Either "courseWork", "courseWorkMaterials" or "announcement".
  login_hint?: string; // The Google ID of the current user.
}

export interface GoogleClassroomAttachmentDiscoveryQueryParams extends GoogleClassroomIframeAddonAttachmentQueryParams {
  addOnToken: string; // A token used to authorize certain Classroom add-on actions.
}

export interface GoogleClassroomAttachmentViewQueryParams extends GoogleClassroomIframeAddonAttachmentQueryParams {
  attachmentId: string;
}

@Injectable()
export class GoogleClassroomGuard {
  store = inject(Store);
  user = select(LoggedInUserState.getLoggedInUser);
  userIsLoggedIn = select(LoggedInUserState.isUserLoggedIn);
  gcService = inject(GoogleClassroomService);

  async canActivate(route: ActivatedRouteSnapshot, routerState: RouterStateSnapshot) {
    const ifqp = this.gcService.readRouteForGoogleClassroomQueryParams(route);

    const userGoogleSubId = this.user()?.ssoProfile?.googleSub;

    const isLoggedIn = this.userIsLoggedIn();
    const isAppInGCContext = this.gcService.isAppInGoogleClassroomIframeContext();
    const isSessionNotGoogleUser = ifqp ? ifqp?.login_hint !== userGoogleSubId : false;

    const logoutExistingGoogleSession = isLoggedIn && isAppInGCContext && isSessionNotGoogleUser;

    if (logoutExistingGoogleSession) {
      console.debug(`GC User does not match cached user profile, logging user out of plasma games`);
      this.store.dispatch(new LogoutUser());
      return false;
    }

    return true;
  }
}

@Injectable({
  providedIn: 'root',
})
export class GoogleClassroomService {
  protected readonly router = inject(Router);
  protected readonly route = inject(ActivatedRoute);
  protected readonly assignActivityToGoogleClassroomAttachmentGql = inject(AssignActivityAsGoogleClassroomAttachmentGQL);
  protected readonly ncs = inject(NetworkCallService);
  protected readonly store = inject(Store);

  protected readonly queryParams = toSignal(this.route.queryParams);

  protected readonly user = select(LoggedInUserState.getLoggedInUser);

  public readonly inGoogleClassroomIframeContext: Signal<boolean> = computed<boolean>(() => {
    return this.getCommonIframeQueryParamKeys().every((key) => this.initialQueryParams()?.[key]);
  });
  public readonly initialQueryParams = signal<{ addOnToken?: string; itemId: string; login_hint?: string; courseId: string; itemType?: string; attachmentId?: string } | null>(
    null
  );
  public readonly loginHint = computed<string | null | undefined>(() => this.initialQueryParams()?.['login_hint']);

  constructor() {
    this.readRouteForGoogleClassroomQueryParams(this.route.snapshot);
  }

  public readRouteForGoogleClassroomQueryParams(
    snapshot: ActivatedRouteSnapshot
  ): { addOnToken?: string; courseId?: string; itemId?: string; itemType?: string; login_hint?: string; attachmentId?: string } | undefined {
    const qp = snapshot.queryParams;

    if (!qp) {
      return;
    }

    let { redirectUrl, courseId, itemId, itemType, login_hint, addOnToken, attachmentId } = qp;

    if ((!courseId || !itemId || !itemType) && redirectUrl) {
      console.debug(`Checking for query params in redirect url, ${qp}`);
      const redirectUrlQueryParams = new URLSearchParams(redirectUrl.split('?')[1]);
      courseId = redirectUrlQueryParams.get('courseId');
      itemId = redirectUrlQueryParams.get('itemId');
      itemType = redirectUrlQueryParams.get('itemType');
      login_hint = redirectUrlQueryParams.get('login_hint');
      addOnToken = redirectUrlQueryParams.get('addOnToken');
      attachmentId = redirectUrlQueryParams.get('attachmentID');
    }

    if (courseId && itemId && itemType) {
      console.debug(`Google Classroom context detected`, this.user());
      const ifqp = {
        addOnToken,
        courseId,
        itemId,
        itemType,
        login_hint,
        attachmentId,
      };

      this.initialQueryParams.set(ifqp);

      return ifqp;
    } else {
      return undefined;
    }
  }

  public isAppInGoogleClassroomIframeContext(): boolean {
    return this.inGoogleClassroomIframeContext();
  }

  public getCommonIframeQueryParamKeys(): ['courseId', 'itemId', 'itemType'] {
    return ['courseId', 'itemId', 'itemType'];
  }

  public getTeacherAttachmentDiscoveryQueryParamKeys(): string[] {
    return [...this.getCommonIframeQueryParamKeys(), 'addOnToken'];
  }

  public getAttachmentViewQueryParamKeys(): string[] {
    return [...this.getCommonIframeQueryParamKeys(), 'attachmentId'];
  }

  public getTeacherAttachmentDiscoveryQueryParams(route: ActivatedRouteSnapshot): GoogleClassroomAttachmentDiscoveryQueryParams {
    const qpm = route.queryParamMap;
    const paramKeys = this.getTeacherAttachmentDiscoveryQueryParamKeys();
    return paramKeys.reduce(
      (out: GoogleClassroomAttachmentDiscoveryQueryParams, key: string): GoogleClassroomAttachmentDiscoveryQueryParams => ({
        ...out,
        [key]: qpm.get(key),
      }),
      {} as GoogleClassroomAttachmentDiscoveryQueryParams
    );
  }

  public getStudentAttachmentViewQueryParams(route: ActivatedRouteSnapshot) {
    const qpm = route.queryParamMap;
    const paramKeys = this.getAttachmentViewQueryParamKeys();
    return paramKeys.reduce(
      (out: GoogleClassroomAttachmentDiscoveryQueryParams, key: string): GoogleClassroomAttachmentDiscoveryQueryParams => ({
        ...out,
        [key]: qpm.get(key),
      }),
      {} as GoogleClassroomAttachmentDiscoveryQueryParams
    );
  }

  public assignActivityToGoogleClassroomAttachment(
    assign: AssignActivityToGoogleClassroomAssignmentDto
  ): Observable<MutationResult<AssignActivityAsGoogleClassroomAttachmentMutation>> {
    return this.ncs.mutateWithSpinner(() => this.assignActivityToGoogleClassroomAttachmentGql.mutate({ assign }));
  }
}
